import type { Article } from "../types";

export const startArticles: Article[] = [
  {
    id: "welcome",
    title: "Welcome to Flowio: what it is and how it fits together",
    category: "start",
    level: "start",
    summary: "The big picture in five minutes: what Flowio does, the words it uses, and where everything lives.",
    tags: ["overview", "introduction", "new", "beginner", "what is flowio", "start", "basics"],
    body: [
      { type: "p", text: "Flowio is an operating system for a sales and service agency, built to work hand in hand with **GoHighLevel** (GHL). Your GHL account holds your contacts and pipelines. Flowio sits on top of it so you can sell to accounts, deliver the work, keep score and train your team, with your actions mirrored back to GHL as contact fields, notes and tags." },
      { type: "h", text: "The words you will see" },
      { type: "glossary", items: [
        { term: "Agency", def: "Your workspace. Everything in Flowio belongs to one agency, and each agency's data is completely separate. You can belong to more than one and switch from the top left." },
        { term: "Account", def: "A prospect or customer. It mirrors a contact in GHL. Most pages are about one account at a time." },
        { term: "Active account", def: "The account you are currently working on. It shows as a chip at the top of the screen, and pages like Live Call use it automatically." },
        { term: "Pipeline", def: "The stages an account moves through, from new lead to paying customer." },
        { term: "Portal contact", def: "A customer's own login to a separate, simple portal where they tick checklists and send you information." },
      ] },
      { type: "h", text: "Where everything lives" },
      { type: "p", text: "The left sidebar groups the app into zones. Some pages only show for certain roles, or if your agency has turned them off." },
      { type: "table", head: ["Zone", "What it is for", "Pages"], rows: [
        ["Home", "Your starting point each day", "Today, Pipeline"],
        ["Sales", "Working prospects and running calls", "Live Call, Revenue Calculator, VSL Hosting, Contract Builder"],
        ["Delivery", "Serving customers after the sale", "Fulfillment, Checklist Templates, Client Portal"],
        ["Insights", "Knowing your numbers", "Command Center, Sales Tracking, Setter Tracking, Booking Board, Status Report, Audit Reports"],
        ["Learning", "Sharpening the sales motion", "Wiki, Playbook, Drill Range, Objections, Mastery"],
        ["Admin", "Configuration", "Settings, Data Admin"],
      ] },
      { type: "callout", tone: "tip", text: "You never have to memorise any of this. Press **?** on any page for help with that page, or press **Ctrl+K** (**⌘K** on a Mac) to jump straight to any account or page." },
      { type: "steps", items: [
        { title: "Take a tour of the screen", to: "/help/start-tour", toLabel: "Read the tour" },
        { title: "Follow the first-hour checklist", to: "/help", toLabel: "Open the checklist" },
      ] },
    ],
    related: ["start-tour", "start-shortcuts", "glossary"],
  },

  {
    id: "start-tour",
    title: "A tour of the screen",
    category: "start",
    level: "start",
    summary: "What every part of the Flowio screen is and does: the sidebar, the top bar, the quick-add button and the prospect panel.",
    tags: ["layout", "navigation", "sidebar", "menu", "header", "quick add", "drawer", "mobile"],
    body: [
      { type: "h", text: "The sidebar (left)" },
      { type: "list", items: [
        "**The agency name at the top** opens the agency switcher. From there you can move between agencies, create a new one, or open team management.",
        "**Home**, then the zones (Sales, Delivery, Insights, Learning, Admin) list the pages you can use. The small dot on a page marks a hero feature.",
        "**Help center** opens this Help Center.",
        "**Quick jump** opens search (Ctrl+K or ⌘K).",
        "On a phone the sidebar opens from the menu button, and a bottom bar gives one-tap access to Today, Pipeline, Call, Setter and Track.",
      ] },
      { type: "h", text: "The top bar" },
      { type: "list", items: [
        "**The account chip** shows which account you are working on. Click the name to open that account, the arrow to switch account, and the X to clear it. When no account is selected it pulses as a reminder.",
        "**Search** opens the same search as Ctrl+K: accounts, calls, notes and pages.",
        "**Sync** (shown only if your role allows syncing) pulls new accounts or the calendar from GoHighLevel without visiting Settings. If GHL is not connected yet, it takes you to Settings to connect it.",
        "**The bell** shows notifications, for example bookings, portal submissions, checklists that stalled, and finished or stopped automations.",
        "**The help button (?)** opens help for the page you are on.",
        "**The sun or moon** switches between light and dark mode.",
        "**Your initial** opens your menu, where you can sign out.",
      ] },
      { type: "h", text: "The quick-add button" },
      { type: "p", text: "The round **+** button at the bottom right is for capturing things fast: **Log a call**, **New prospect**, **Audit reports** and **Setter cockpit**." },
      { type: "h", text: "The prospect panel" },
      { type: "p", text: "Clicking a lead or booking on Today, the Booking Board or Sales Tracking slides open a panel on the right with that account's summary, research and quick actions, without leaving the page. Its address ends in `?prospect=<account id>`, so you can share or bookmark it." },
      { type: "callout", tone: "note", text: "Sidebar pages vary by person. Your role, and which modules your agency has enabled, decide what you see." },
    ],
    related: ["welcome", "start-shortcuts"],
  },

  {
    id: "start-shortcuts",
    title: "Keyboard shortcuts and quick moves",
    category: "start",
    level: "start",
    summary: "The few shortcuts that save the most time.",
    tags: ["keyboard", "shortcut", "hotkey", "search", "cmd k", "ctrl k"],
    body: [
      { type: "table", head: ["Press", "What happens"], rows: [
        ["Ctrl+K or ⌘K", "Opens quick search. Type two or more letters to find accounts, calls and notes, or jump to a page."],
        ["/", "Also opens quick search, as long as you are not typing in a box."],
        ["?", "Opens help for the current page, as long as you are not typing in a box."],
        ["Esc", "Closes search, menus and most panels, and cancels an inline edit."],
        ["Enter", "Saves a single-line inline edit. In a multi-line box, Ctrl+Enter or ⌘+Enter saves."],
      ] },
      { type: "callout", tone: "tip", text: "Quick search is the fastest way around. If you know the business name, press Ctrl+K, type a few letters, and press Enter." },
    ],
    related: ["start-tour", "welcome"],
  },
];
